import React from 'react';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { LuChevronDown } from 'react-icons/lu';
import styles from './StanPhoneInput.module.css';
import inputStyles from '@/components/core-components/StanInput/StanInput.module.css';
import { cn } from '@/lib/utils/cn';
import { useStanPhoneInput } from './hooks/useStanPhoneInput';
import type { StanPhoneInputProps } from './StanPhoneInput.types';

export const StanPhoneInput: React.FC<StanPhoneInputProps> = ({
    id,
    name,
    value,
    label,
    placeholder = 'Phone Number',
    disabled,
    error,
    hintText,
    onChange,
    className,
    defaultCountry,
}) => {
    const {
        options,
        dropdownValue,
        countryDial,
        countryImage,
        inputValue,
        handleInputChange,
        handleCountrySelect,
        shouldShowError,
    } = useStanPhoneInput({ value, onChange, error, defaultCountry });

    return (
        <div className={cn(styles.container, className)}>
            {label && (
                <label className={inputStyles.label} htmlFor={id}>
                    {label}
                </label>
            )}
            <div
                className={cn(
                    styles.wrapper,
                    disabled && styles.wrapperDisabled,
                    shouldShowError && styles.wrapperError
                )}
            >
                <div className={styles.selector}>
                    <DropdownProvider
                        initialValue={dropdownValue}
                        dropdownId={`${id}-country`}
                        dropdownName={`${name}-country`}
                    >
                        <StanDropdownMenu
                            id={`${id}-country`}
                            placement='bottom-start'
                            name={`${name}-country`}
                            options={options}
                            onSelect={handleCountrySelect}
                            className={styles.dropdownMenu}
                            currentValue={dropdownValue}
                            minWidth={220}
                            rounded
                        >
                            <div className={cn(styles.dropdownTrigger, disabled && styles.triggerDisabled)}>
                                <div className={styles.triggerContent}>
                                    <img src={countryImage} alt={countryDial} className={styles.flag} />
                                    <span className={styles.dialCode}>{countryDial}</span>
                                </div>
                                <LuChevronDown className={styles.chevron} />
                            </div>
                        </StanDropdownMenu>
                    </DropdownProvider>
                </div>
                <input
                    id={id}
                    name={name}
                    value={inputValue}
                    onChange={e => handleInputChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoComplete='tel'
                    className={cn(inputStyles.field, styles.input)}
                />
            </div>
            {shouldShowError && (
                <span className={cn(inputStyles.hint, styles.hintError)}>
                    {hintText || 'Enter a valid phone number'}
                </span>
            )}
        </div>
    );
};

export type { PhoneChange } from './StanPhoneInput.types';
